/**
 * Staff della squadra (V44): Mister e Dirigenti di questa squadra-stagione.
 *
 * E la stessa assegnazione di V51 vista dalla squadra: legge l'elenco degli
 * account della societa e ne tiene i ruoli su questa squadra, cosi che le
 * due viste non possano mostrare staff diversi. Si assegna un account gia
 * esistente e attivo; chi ha gia un ruolo qui non compare tra i candidati.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { UserMinusIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ComandoIcona, PulsanteNuovo } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { messaggio, useDati } from '../comuni/dati.js';
import { useContesto } from '../navigazione/contesto.js';
import { FormRuolo } from './FormRuolo.js';
import {
  type AccountSocieta,
  ETICHETTA_RUOLO,
  type ElencoAccount,
  type RuoloAssegnato,
  candidatiStaff,
  nomeSquadra,
  staffDi,
} from './ruoli.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

export function Staff() {
  const { ss } = useParams<{ ss: string }>();
  const { squadre, ricaricaSquadre } = useContesto();
  const { dati, errore, ricarica } = useDati<ElencoAccount>('/amministrazione/account');
  const [aggiunta, setAggiunta] = useState(false);
  const [inRevoca, setInRevoca] = useState<string | null>(null);

  // Il guscio di squadra non ha margini: ogni vista mette i suoi.
  if (!dati || !ss) {
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );
  }

  const squadra = squadre?.find((s) => s.id === ss);
  const staff = staffDi(dati.account, ss);
  const candidati = candidatiStaff(dati.account, ss);
  const aggiorna = () => Promise.all([ricarica(), ricaricaSquadre()]);

  const revoca = async (account: AccountSocieta, r: RuoloAssegnato) => {
    setInRevoca(r.id);
    try {
      await chiama(`/amministrazione/account/${account.id}/ruoli/${r.id}`, { metodo: 'DELETE' });
      await aggiorna();
      toast.success('Ruolo revocato.');
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setInRevoca(null);
    }
  };

  const assegna = (
    <PulsanteNuovo
      etichetta="Assegna ruolo"
      onClick={() => setAggiunta(true)}
      disabled={candidati.length === 0}
    />
  );

  return (
    <div>
      <Testata titolo="Staff" voce="staff" azione={staff.length > 0 && assegna} />
      <Corpo>
        <div className="flex flex-col gap-4">
          {staff.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>Nessuno nello staff</EmptyTitle>
                <EmptyDescription>
                  Finché non ha un Mister o un Dirigente, questa squadra la vede solo l'Admin.
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>{assegna}</EmptyContent>
            </Empty>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>Ruolo</TableHead>
                  <TableHead>
                    <span className="sr-only">Azioni</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map(({ account, ruolo }) => (
                  <TableRow key={ruolo.id}>
                    <TableCell>
                      <Link
                        to={`/amministrazione/account/${account.id}/ruoli`}
                        className="flex flex-col hover:underline"
                      >
                        <span className="flex items-center gap-2 font-medium">
                          {account.cognome} {account.nome}
                          {!account.attivo && <Badge variant="secondary">Disattivato</Badge>}
                        </span>
                        <span className="text-xs text-muted-foreground">{account.email}</span>
                      </Link>
                    </TableCell>
                    <TableCell>{ETICHETTA_RUOLO[ruolo.ruolo]}</TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <ComandoIcona
                          etichetta="Revoca il ruolo"
                          Icona={UserMinusIcon}
                          size="icon-sm"
                          disabled={inRevoca === ruolo.id}
                          onClick={() => void revoca(account, ruolo)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {candidati.length === 0 && (
            <StatoVuoto
              titolo="Nessun account da aggiungere"
              descrizione="Tutti gli account attivi della società hanno già un ruolo in questa squadra."
            />
          )}
        </div>

        <Dialog open={aggiunta} onOpenChange={setAggiunta}>
          <DialogContent>
            {aggiunta && (
              <FormRuolo
                titolo="Assegna allo staff"
                descrizione={
                  squadra
                    ? `Il ruolo vale solo su ${nomeSquadra(squadra)}, stagione ${squadra.stagione}.`
                    : 'Il ruolo vale solo su questa squadra-stagione.'
                }
                etichettaScelta="Account"
                gruppi={[
                  {
                    titolo: null,
                    opzioni: candidati.map((a) => ({
                      valore: a.id,
                      etichetta: `${a.cognome} ${a.nome}`,
                    })),
                  },
                ]}
                onChiudi={() => setAggiunta(false)}
                onInvia={async (ruolo, accountId) => {
                  await chiama(`/amministrazione/account/${accountId}/ruoli`, {
                    metodo: 'POST',
                    corpo: { ruolo, squadraStagioneId: ss },
                  });
                  setAggiunta(false);
                  await aggiorna();
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </Corpo>
    </div>
  );
}
